import { useState } from 'react'
import {
  clampQuantity,
  MAX_AXLE_QUANTITY,
  parseTyreSize,
  type TyreSelection,
} from '../lib/tyres'

export const INVALID_SIZE_MESSAGE = 'That doesn’t look like a tyre size.'

export interface TyreSizeEntryProps {
  selection: TyreSelection | null
  onSelectionChange: (selection: TyreSelection | null) => void
  /** Ask phones for the number pad. */
  numericKeyboard?: boolean
}

/**
 * Typing the size off the sidewall, with an optional different rear (ACA-98).
 *
 * Validates as the customer types so the fitter never receives "dunno, the
 * normal ones". A staggered car needs both sizes; typing the same size twice
 * is one size all round.
 */
export function TyreSizeEntry({
  selection,
  onSelectionChange,
  numericKeyboard = false,
}: TyreSizeEntryProps) {
  const manual = selection?.size.source === 'manual' ? selection : null
  const [frontInput, setFrontInput] = useState(manual?.size.size ?? '')
  const [rearInput, setRearInput] = useState(manual?.rear?.size ?? '')
  const [rearOn, setRearOn] = useState(Boolean(manual?.rear))
  const [frontError, setFrontError] = useState<string | null>(null)
  const [rearError, setRearError] = useState<string | null>(null)

  const commit = (front: string, rear: string, withRear: boolean) => {
    const parsedFront = parseTyreSize(front)
    const parsedRear = withRear ? parseTyreSize(rear) : null
    setFrontError(!parsedFront && front.trim() ? INVALID_SIZE_MESSAGE : null)
    setRearError(withRear && !parsedRear && rear.trim() ? INVALID_SIZE_MESSAGE : null)

    if (!parsedFront || (withRear && !parsedRear)) {
      // Drop a previously accepted typed size so the customer cannot continue
      // with a stale value after editing it into nonsense.
      if (manual) onSelectionChange(null)
      return
    }

    if (withRear && parsedRear && parsedRear !== parsedFront) {
      onSelectionChange({
        size: { size: parsedFront, source: 'manual', front: true, rear: false },
        quantity: manual?.rear ? manual.quantity : MAX_AXLE_QUANTITY,
        rear: {
          size: parsedRear,
          quantity: manual?.rear ? manual.rear.quantity : MAX_AXLE_QUANTITY,
        },
      })
      return
    }

    // Two, not four, for a typed size. Pairs are the common purchase, one axle
    // at a time, and defaulting to a full set doubles the first price shown.
    onSelectionChange({
      size: { size: parsedFront, source: 'manual', front: true, rear: true },
      quantity: manual && !manual.rear ? clampQuantity(manual.quantity) : 2,
    })
  }

  const frontDescribedBy = frontError ? 'ac-tyre-size-error' : 'ac-tyre-size-hint'

  return (
    <div className="ac-stack ac-stack--tight">
      <div className="ac-field">
        <label className="ac-label" htmlFor="ac-tyre-size">
          {rearOn ? 'Front tyre size' : 'Tyre size'}
        </label>
        <input
          id="ac-tyre-size"
          className="ac-input"
          value={frontInput}
          onChange={(event) => {
            setFrontInput(event.target.value)
            commit(event.target.value, rearInput, rearOn)
          }}
          placeholder="205/55R16"
          autoComplete="off"
          inputMode={numericKeyboard ? 'numeric' : undefined}
          aria-invalid={Boolean(frontError)}
          aria-describedby={frontDescribedBy}
        />
        {frontError ? (
          <p className="ac-field-error" id="ac-tyre-size-error">
            {frontError}
          </p>
        ) : (
          <p className="ac-hint" id="ac-tyre-size-hint">
            Three numbers from the sidewall, e.g. 205/55R16.
          </p>
        )}
      </div>

      {rearOn ? (
        <div className="ac-field">
          <label className="ac-label" htmlFor="ac-tyre-size-rear">
            Rear tyre size
          </label>
          <input
            id="ac-tyre-size-rear"
            className="ac-input"
            value={rearInput}
            onChange={(event) => {
              setRearInput(event.target.value)
              commit(frontInput, event.target.value, true)
            }}
            placeholder="255/35R19"
            autoComplete="off"
            inputMode={numericKeyboard ? 'numeric' : undefined}
            aria-invalid={Boolean(rearError)}
            aria-describedby={rearError ? 'ac-tyre-size-rear-error' : undefined}
          />
          {rearError ? (
            <p className="ac-field-error" id="ac-tyre-size-rear-error">
              {rearError}
            </p>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        className="ac-btn ac-btn--ghost"
        aria-expanded={rearOn}
        onClick={() => {
          const next = !rearOn
          setRearOn(next)
          if (!next) setRearError(null)
          commit(frontInput, rearInput, next)
        }}
      >
        {rearOn ? 'Same size front and rear' : 'My rear tyres are a different size'}
      </button>
    </div>
  )
}
